import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { ExpandMore } from '@mui/icons-material'
import { Box, Divider, IconButton, Typography } from '@mui/material'

import { selectMiscItems } from '@/app/services/miscItems'
import { selectQuoteItems } from '@/app/services/quoteItems'
import { selectQuote, useCalculateDeliveryPriceMutation, useCalculateTaxRateMutation } from '@/app/services/quotes'
import { useGetTaxRatesQuery } from '@/app/services/taxRates'
import { selectLocale } from '@/app/slices/appSlice'
import {
    selectCurrencyCode,
    selectOrganisation,
    selectOrganisationId,
    selectPaidFeatures,
} from '@/app/slices/organisationSlice'
import { selectedQuoteStatus, selectIsCalculatingQuote } from '@/app/slices/quoteItemsSlice'
import { getSecondaryProcessTotals } from '@/common/helpers/secondaryProcessUtilities'
import { useCurrencyFormatter, useToolBoxTreatments } from '@/common/hooks'
import { fractionToPercentage, getDecimalPlacesForDisplay, QUOTE_STATUS_ARRAY } from '@/common/utils'
import { isQuoteItemCalculated } from '@/common/utils/quoteUtilities'

import QuoteAdjustmentEditor from './QuoteAdjustmentEditor'

const classes = {
    pricesContainer: {
        borderRadius: 2,
        border: '1px solid #263238',
        background: '#34495E',
        color: '#FFFFFF',
        // A minimum, not a fixed height, so the adjustment editor can open inline
        minHeight: '624px',
    },
    pricePercentage: {
        fontSize: '14px',
    },
    bold: {
        fontWeight: 'bold',
    },
    itemColumn: {
        display: 'flex',
        flex: 1,
    },
    alignRight: {
        justifyContent: 'flex-end',
    },
    divider: {
        borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    // A white card marks the one editable part of this dark, read-only panel
    adjustmentCard: {
        backgroundColor: '#FFFFFF',
        border: 2,
        borderColor: 'secondary.main',
        borderRadius: 2,
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mx: -1.5,
        px: 2,
        py: 1.75,
    },
}

const ItemRow = ({ children }) => (
    <Box
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
        }}
    >
        {children}
    </Box>
)

const PriceTotals = () => {
    const { t } = useTranslation()
    const { quoteId } = useParams()
    const { showFolding, showMiscItems, showMultipleTaxRates, showSecondaryProcesses } = useToolBoxTreatments()

    const organisation = useSelector(selectOrganisation)
    const organisationId = useSelector(selectOrganisationId)
    const paidFeatures = useSelector(selectPaidFeatures)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const [, { isLoading: isCalculatingDelivery }] = useCalculateDeliveryPriceMutation({
        fixedCacheKey: 'shared-calculate-delivery-price',
    })
    const [, { isLoading: isCalculatingTax }] = useCalculateTaxRateMutation({
        fixedCacheKey: 'shared-calculate-tax-rate',
    })

    const quoteStatus = useSelector(selectedQuoteStatus)
    const isCalculatingQuote = useSelector(selectIsCalculatingQuote)

    const hidePrices = quoteStatus === QUOTE_STATUS_ARRAY.NotCalculated || isCalculatingDelivery || isCalculatingTax

    // Adjusting every item needs fresh line prices, which calculated and draft quotes have
    let adjustmentDisabledReason = null
    if (isCalculatingQuote) {
        adjustmentDisabledReason = t('Wait for the calculation to finish.')
    } else if (quoteStatus === QUOTE_STATUS_ARRAY.NotCalculated) {
        adjustmentDisabledReason = t('Calculate the quote first.')
    } else if (![QUOTE_STATUS_ARRAY.Calculated, QUOTE_STATUS_ARRAY.Draft].includes(quoteStatus)) {
        adjustmentDisabledReason = t('Only calculated or draft quotes can be adjusted.')
    }

    const selectedQuote = useSelector((state) =>
        selectQuote(state, { organisationId: organisation.organisationId, quoteId })
    )

    const quoteItems = useSelector((state) =>
        selectQuoteItems(state, { organisationId: organisation.organisationId, quoteId })
    )

    // The API sends the misc items total with their adjustments already in it, and the parts
    // adjustment on its own. Taking the misc adjustments back out lets every adjustment show in one row.
    const miscAdjustments = useSelector((state) =>
        selectMiscItems(state, { organisationId: organisation.organisationId, quoteId })
            .filter((item) => !item.isDeleted)
            .reduce((sum, item) => sum + (item.quantity ?? 1) * (item.priceAdjustment ?? 0), 0)
    )

    const { data: taxRates } = useGetTaxRatesQuery({ organisationId, params: { includeDeleted: true } })

    const areAllItemsCalculated = useMemo(() => {
        return quoteItems?.every(isQuoteItemCalculated)
    }, [quoteItems])

    const secondaryProcessTotals = useMemo(() => getSecondaryProcessTotals(quoteItems), [quoteItems])

    // null until the user toggles it, so the default follows the number of processes as items load
    const [isBreakdownToggledOpen, setIsBreakdownToggledOpen] = useState(null)
    const isBreakdownOpen = isBreakdownToggledOpen ?? secondaryProcessTotals.length >= 2

    const isMiscItemsEnabled = showMiscItems && paidFeatures?.hasMiscellaneousItems

    const showMinimumQuoteChargePrice = Boolean(selectedQuote.minimumQuoteChargePrice)

    const showDeliveryPrice =
        selectedQuote?.deliveryOption === 'Delivery' && selectedQuote?.deliveryPricingMethod === 'ChargeToOrder'

    const miscItemsPrice = (selectedQuote.miscellaneousItemsTotalPrice || 0) - miscAdjustments

    const subtotal = useMemo(
        () =>
            (selectedQuote.materialPrice || 0) +
            (selectedQuote.processingTotalPrice || 0) +
            (selectedQuote.foldingTotalPrice || 0) +
            (selectedQuote.secondaryProcessTotalPrice || 0) +
            miscItemsPrice,
        [selectedQuote, miscItemsPrice]
    )

    const adjustments = (selectedQuote.otherChargeTotalPrice || 0) + miscAdjustments

    const totalWithoutTax = selectedQuote.linePrice

    const quoteMinimumChargePrice = selectedQuote.minimumQuoteChargePrice || 0

    const deliveryPrice = selectedQuote.deliveryPrice || 0

    const subTotalWithDiff = totalWithoutTax + quoteMinimumChargePrice

    const total = selectedQuote.lineTaxedPrice

    const taxes = total - subTotalWithDiff

    const quoteTaxRate = useMemo(
        () =>
            showMultipleTaxRates && selectedQuote?.taxRateId
                ? (taxRates?.find((taxRate) => taxRate.id === selectedQuote?.taxRateId)?.percentage ??
                  taxes / totalWithoutTax)
                : taxes / totalWithoutTax,
        [selectedQuote.taxRateId, showMultipleTaxRates, taxRates, taxes, totalWithoutTax]
    )

    const quoteTaxRateName = useMemo(
        () =>
            showMultipleTaxRates && selectedQuote?.taxRateId
                ? (taxRates?.find((taxRate) => taxRate.id === selectedQuote?.taxRateId)?.displayName ?? t('Tax'))
                : t('Tax'),
        [selectedQuote.taxRateId, showMultipleTaxRates, taxRates, t]
    )

    const decimalPlaces = useMemo(() => getDecimalPlacesForDisplay(quoteTaxRate), [quoteTaxRate])

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'space-between',
                    p: 3,
                },
                classes.pricesContainer,
            ]}
        >
            <ItemRow>
                <Typography
                    data-testid="price-totals-section-title"
                    variant="strong1"
                >
                    {t('PRICE TOTALS')}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-material-price-label"
                    sx={classes.itemColumn}
                    variant="body2"
                >
                    {t('Material')}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated
                        ? fractionToPercentage(selectedQuote.materialPrice / subtotal, 0)
                        : null}
                    %
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated && selectedQuote.materialPrice
                        ? formatCurrency(selectedQuote.materialPrice)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-cutting-price-label"
                    sx={classes.itemColumn}
                    variant="body2"
                >
                    {t('Cutting')}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated
                        ? fractionToPercentage(selectedQuote.processingTotalPrice / subtotal, 0)
                        : null}
                    %
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated && selectedQuote.processingTotalPrice
                        ? formatCurrency(selectedQuote.processingTotalPrice)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            {showFolding ? (
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-folding-price-label"
                        sx={classes.itemColumn}
                        variant="body2"
                    >
                        {t('Folding')}
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated
                            ? fractionToPercentage(selectedQuote.foldingTotalPrice / subtotal, 0)
                            : null}
                        %
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated && selectedQuote.foldingTotalPrice
                            ? formatCurrency(selectedQuote.foldingTotalPrice)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
            ) : null}
            {showSecondaryProcesses ? (
                <ItemRow>
                    <Box sx={[classes.itemColumn, { alignItems: 'center' }]}>
                        <Typography
                            data-testid="nqp-quote-secondary-process-price-label"
                            sx={{ whiteSpace: 'nowrap' }}
                            variant="body2"
                        >
                            {t('Secondary processes')}
                        </Typography>
                        {!hidePrices && areAllItemsCalculated && secondaryProcessTotals.length ? (
                            <IconButton
                                aria-expanded={isBreakdownOpen}
                                aria-label={t('Secondary process breakdown')}
                                color="inherit"
                                size="small"
                                sx={{ p: 0, ml: 0.5 }}
                                onClick={() => setIsBreakdownToggledOpen(!isBreakdownOpen)}
                            >
                                <ExpandMore
                                    fontSize="small"
                                    sx={{ transform: isBreakdownOpen ? 'rotate(180deg)' : 'none' }}
                                />
                            </IconButton>
                        ) : null}
                    </Box>
                    <Typography
                        sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated
                            ? fractionToPercentage(selectedQuote.secondaryProcessTotalPrice / subtotal, 0)
                            : null}
                        %
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated && selectedQuote.secondaryProcessTotalPrice
                            ? formatCurrency(selectedQuote.secondaryProcessTotalPrice)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
            ) : null}
            {showSecondaryProcesses &&
            !hidePrices &&
            areAllItemsCalculated &&
            isBreakdownOpen &&
            secondaryProcessTotals.length ? (
                // The card has a fixed height: as a scroll container the list may shrink to the room left and
                // scroll, instead of being squeezed and drawing over the rows below it
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexShrink: 1,
                        gap: 0.25,
                        mt: -1.5,
                        overflowY: 'auto',
                        pl: 2,
                    }}
                >
                    {secondaryProcessTotals.map(({ secondaryProcess, secondaryProcessId, total }) => (
                        <Box
                            data-testid="nqp-quote-secondary-process-row"
                            key={secondaryProcessId}
                            sx={{ display: 'flex', flexShrink: 0, justifyContent: 'space-between', opacity: 0.8 }}
                        >
                            <Typography variant="body2">{secondaryProcess?.name}</Typography>
                            <Typography variant="body2">{formatCurrency(total)}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
            {isMiscItemsEnabled ? (
                <>
                    <Divider
                        orientation="horizontal"
                        sx={classes.divider}
                        variant="fullWidth"
                    />
                    <ItemRow>
                        <Typography
                            data-testid="nqp-quote-other-price-label"
                            sx={classes.itemColumn}
                            variant="body2"
                        >
                            {t('Miscellaneous items')}
                        </Typography>
                        <Typography
                            sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                            variant="strong2"
                        >
                            {!hidePrices && areAllItemsCalculated
                                ? fractionToPercentage(miscItemsPrice / subtotal, 0)
                                : null}
                            %
                        </Typography>
                        <Typography
                            sx={[classes.itemColumn, classes.alignRight]}
                            variant="strong2"
                        >
                            {!hidePrices && areAllItemsCalculated && miscItemsPrice
                                ? formatCurrency(miscItemsPrice)
                                : String.fromCharCode(8212)}
                        </Typography>
                    </ItemRow>
                </>
            ) : null}
            <Divider
                orientation="horizontal"
                sx={classes.divider}
                variant="fullWidth"
            />
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-subtotal-price-label"
                    sx={classes.itemColumn}
                    variant="body2"
                >
                    {t('Subtotal')}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated ? 100 : null}%
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated && selectedQuote.linePrice
                        ? formatCurrency(subtotal)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <Box sx={classes.adjustmentCard}>
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-adjustment-price-label"
                        sx={classes.itemColumn}
                        variant="body2"
                    >
                        {t('Adjustment')}
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated ? fractionToPercentage(adjustments / subtotal, 2) : null}%
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated && selectedQuote.otherChargeTotalPrice
                            ? formatCurrency(adjustments)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
                <QuoteAdjustmentEditor
                    disabledReason={adjustmentDisabledReason}
                    subtotal={subtotal}
                />
            </Box>
            <Divider
                orientation="horizontal"
                sx={classes.divider}
                variant="fullWidth"
            />
            {showMinimumQuoteChargePrice ? (
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-minimum-charge-price-label"
                        sx={classes.itemColumn}
                        variant="body2"
                    >
                        {t('Minimum order charge')}
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated
                            ? fractionToPercentage(quoteMinimumChargePrice / totalWithoutTax, 0)
                            : null}
                        %
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated && organisation.minimumQuoteChargePrice
                            ? formatCurrency(quoteMinimumChargePrice)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
            ) : null}
            {showDeliveryPrice ? (
                <ItemRow>
                    <Typography
                        data-testid="nqp-quote-minimum-charge-price-label"
                        sx={classes.itemColumn}
                        variant="body2"
                    >
                        {t('Delivery')}
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated
                            ? fractionToPercentage(deliveryPrice / subtotal, 0)
                            : null}
                        %
                    </Typography>
                    <Typography
                        sx={[classes.itemColumn, classes.alignRight]}
                        variant="strong2"
                    >
                        {!hidePrices && areAllItemsCalculated && selectedQuote.deliveryPrice
                            ? formatCurrency(deliveryPrice)
                            : String.fromCharCode(8212)}
                    </Typography>
                </ItemRow>
            ) : null}
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-subtotal-price-label"
                    sx={classes.itemColumn}
                    variant="body2"
                >
                    {t('Subtotal')}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.pricePercentage, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated ? fractionToPercentage(subTotalWithDiff / subtotal, 2) : null}
                    %
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated && totalWithoutTax
                        ? formatCurrency(subTotalWithDiff)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-tax-price-label"
                    sx={classes.itemColumn}
                    variant="body2"
                >
                    {quoteTaxRateName}{' '}
                    {t(`(${quoteTaxRate ? `${fractionToPercentage(quoteTaxRate, decimalPlaces)}%` : '0%'})`)}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong2"
                >
                    {!hidePrices && areAllItemsCalculated && taxes ? formatCurrency(taxes) : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
            <Divider
                orientation="horizontal"
                sx={classes.divider}
                variant="fullWidth"
            />
            <ItemRow>
                <Typography
                    data-testid="nqp-quote-total-price-label"
                    sx={classes.itemColumn}
                    variant="strong1"
                >
                    {t('Total')}
                </Typography>
                <Typography
                    sx={[classes.itemColumn, classes.alignRight]}
                    variant="strong1"
                >
                    {!hidePrices && areAllItemsCalculated && selectedQuote.lineTaxedPrice
                        ? formatCurrency(selectedQuote.lineTaxedPrice)
                        : String.fromCharCode(8212)}
                </Typography>
            </ItemRow>
        </Box>
    )
}
export default memo(PriceTotals)
